import { produce } from 'immer';

import { preferencesActionTypes } from './preferences.types';
import { PREF_FIELDS } from './preferences.fields';

// Defaults match the target backend schema (see
// docs/users/preferences-schema-extension.md §2). These are only used before
// the first GET completes. Keys the backend does not return yet keep these
// defaults (all assignments guard undefined).
const initialState = {
    theme: 'system',
    sidebarCollapsed: false,
    language: 'en',
    timezone: 'America/Los_Angeles',
    notificationsDiagramShared: true,
    notificationsMentions: true,
    notificationsProductUpdates: true,
    // Wire-shaped (snake_case) so PATCH partials merge without renaming.
    // Named `diagram` (not `editor`) — "editor" is reserved for the upcoming
    // code editor's own preference group.
    diagram: {
        input_mode: 'auto',
        background: 'default',
        grid: 'dots',
        show_align_lines: true,
        show_cursors: true,
        show_comments: true,
        show_scrollbars: true,
        show_undo_redo: true,
    },
    systemIsDark: false,         // populated by the matchMedia startup saga
    isInitialized: false,
    isLoading: false,
    error: null,
};

const preferencesReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {
            // FETCH
            case preferencesActionTypes.PREFERENCES_FETCH_REQUEST: {
                draft.isLoading = true;
                draft.error = null;
                break;
            }
            case preferencesActionTypes.PREFERENCES_FETCH_SUCCESS: {
                const p = action.payload || {};
                PREF_FIELDS.forEach(({ wire, store }) => {
                    if (p[wire] !== undefined) draft[store] = p[wire];
                });
                if (p.diagram !== undefined) draft.diagram = { ...draft.diagram, ...p.diagram };
                draft.isLoading = false;
                draft.isInitialized = true;
                draft.error = null;
                break;
            }
            case preferencesActionTypes.PREFERENCES_FETCH_FAILURE: {
                draft.isLoading = false;
                // Mark initialized even on failure so the app doesn't hang.
                draft.isInitialized = true;
                draft.error = action.payload;
                break;
            }

            // PATCH (optimistic update happens here, before the network call)
            case preferencesActionTypes.PREFERENCES_PATCH_REQUEST: {
                const p = action.payload || {};
                PREF_FIELDS.forEach(({ wire, store }) => {
                    if (p[wire] !== undefined) draft[store] = p[wire];
                });
                if (p.diagram !== undefined) draft.diagram = { ...draft.diagram, ...p.diagram };
                break;
            }
            case preferencesActionTypes.PREFERENCES_PATCH_SUCCESS: {
                // Merge the server blob to handle drift. Guards keep local
                // defaults for keys a not-yet-extended backend omits.
                const p = action.payload || {};
                PREF_FIELDS.forEach(({ wire, store }) => {
                    if (p[wire] !== undefined) draft[store] = p[wire];
                });
                if (p.diagram !== undefined) draft.diagram = { ...draft.diagram, ...p.diagram };
                draft.error = null;
                break;
            }
            case preferencesActionTypes.PREFERENCES_PATCH_FAILURE: {
                draft.error = action.payload;
                break;
            }
            case preferencesActionTypes.PREFERENCES_PATCH_REVERT: {
                // Payload uses STORE names (the saga snapshots slice values).
                const prev = action.payload || {};
                PREF_FIELDS.forEach(({ store }) => {
                    if (prev[store] !== undefined) draft[store] = prev[store];
                });
                if (prev.diagram !== undefined) draft.diagram = prev.diagram;
                break;
            }

            // SYSTEM THEME
            case preferencesActionTypes.SYSTEM_THEME_CHANGED: {
                draft.systemIsDark = !!action.payload;
                break;
            }

            // RESET (sign out) — user data resets; systemIsDark survives (the
            // OS theme didn't change, and the matchMedia channel only re-emits
            // on actual changes, so wiping it here would leave the resolved
            // theme wrong until the next OS-theme flip).
            case preferencesActionTypes.PREFERENCES_RESET: {
                return {
                    ...initialState,
                    systemIsDark: state.systemIsDark,
                };
            }

            default:
                break;
        }
    });
};

export default preferencesReducer;
